import { useGetOwfReferralCode } from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireR2R/useGetOwfReferralCode";
import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { Link } from "@faire/slate/icons/Link";
import { url as openWithFaireUrl } from "@faire/web-api--source/routes/www/open-with-faire";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { useStrictLocalization } from "@faire/web/common/localization";
import { TextArea } from "@faire/web/slate-deprecated/TextArea";
import { Flex, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import React, { useRef, useState } from "react";
import styled from "styled-components";

export const OpenWithFaireShareLinkWidget: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile } = useViewport();
  const owfReferralCodeV2 = useGetOwfReferralCode();

  const [copied, setCopied] = useState<boolean>(false);

  const ref = useRef<HTMLTextAreaElement>(null);

  const onCopyButtonClick = () => {
    const textArea = ref.current;

    if (!textArea) {
      return;
    }

    getWindowOrThrow().navigator.clipboard.writeText(textArea.value);

    setCopied(true);

    setTimeoutIfPossible(() => {
      setCopied(false);
    }, 1200);
  };

  const copiedText = strictLocalize({
    id: "IjLI80",
    defaultMessage: "Copied",
    description: {
      text: "Text on a button that will copy the link after being clicked",
    },
  });

  const copyButtonText: string = copied
    ? copiedText
    : strictLocalize({
        id: "fg2jgj",
        defaultMessage: "Copy Link",
        description: {
          text: "Text on a button that will copy the link after being clicked",
        },
      });

  const copyMobileButtonText: string = copied
    ? copiedText
    : strictLocalize({
        id: "6NP5XH",
        defaultMessage: "Copy",
        description: {
          text: "Text on a button that will copy the link after being clicked",
        },
      });

  return (
    <Wrapper>
      <LinkTextContainer>
        <LinkText
          ref={ref}
          readOnly
          value={openWithFaireUrl({ ref: owfReferralCodeV2 })}
        />
      </LinkTextContainer>
      <CopyButton onClick={onCopyButtonClick} role="button">
        {!isMobile ? (
          <Flex flex={1}>
            <Link className="button-icon" />
          </Flex>
        ) : null}
        <Spacer width={4} />
        <Typography color={Core.text.subdued} variant="paragraphSansMedium">
          {isMobile ? copyMobileButtonText : copyButtonText}
        </Typography>
      </CopyButton>
    </Wrapper>
  );
};

const Wrapper = styled(Row).attrs({ justify: "space-between" })`
  background-color: transparent;
  border: 1px solid ${Core.border.muted};
  width: 100%;

  &:hover {
    .button-text,
    .button-icon {
      color: ${Core.text.default};
    }
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 481px;
  }
`;

const LinkTextContainer = styled.div`
  padding: 15px;
  display: flex;
  flex: 1;
`;

const LinkText = styled(TextArea)`
  border: none;
  height: 20px;
  padding: 0;
  margin-right: 8px;
  background-color: transparent;
  color: ${Core.text.inverse};
  overflow: auto;
  white-space: nowrap;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  &:focus {
    border: none;
  }
`;

const CopyButton = styled(Row).attrs({ align: "center" })`
  background-color: ${Core.surface.inverse};
  padding: 15px;
  flex-basis: auto;
  cursor: pointer;
  color: ${Core.text.subdued};
`;
